import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import apis from "../../../utils/apis";
import { handleApiError } from "../utils/utils";
import { showFeedbackModal } from "../UserFeedbackActions";
import {
  FETCH_DEPARTMENTS_REQUEST,
  FETCH_DEPARTMENTS_SUCCESS,
  FETCH_DEPARTMENTS_FAILURE,
} from '../../types';
import { ThunkAction } from 'redux-thunk';
import { RootState } from '../..';
import { decodeHtmlEntities } from '../../../utils/textUtils';
import { translate } from '../../../i18n/translate';
import { readErrorMessage } from '../../../utils/errorMessage';

export const fetchAllDepartments = (params: {
  searchTerm?: string;
  filters?: { [key: string]: string[] };
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
} = {}): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  dispatch({ type: FETCH_DEPARTMENTS_REQUEST });
  try {
    const response = await apis.get('/org-people/departments');

    if (response && response.status === 200 && response.data?.success) {
      const departments = (response.data.departments || []).map((dept: any) => ({
        ...dept,
        name: decodeHtmlEntities(dept.name),
        description: decodeHtmlEntities(dept.description),
      }));
      
      dispatch({
        type: FETCH_DEPARTMENTS_SUCCESS,
        payload: {
          departments,
          total: departments.length,
          currentPage: 1,
          totalPages: 1,
        },
      });
      return departments;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error: any) {
    dispatch({
      type: FETCH_DEPARTMENTS_FAILURE,
      payload: error?.response?.data?.message || 'Failed to fetch departments',
    });
    handleApiError(error, dispatch);
    return [];
  }
};

export const createDepartment = (departmentData: { name: string; description?: string }): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.post('/org-people/departments', departmentData);

    if (response?.data?.success || response?.status === 201 || response?.status === 200) {
      const department = response.data.department || response.data;
      
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: `Department "${departmentData.name}" created successfully`,
          modalType: 'success',
          duration: 3000,
        })
      );
      return department;
    } else {
      throw new Error('Failed to create department');
    }
  } catch (error: any) {
    const errorMsg = error?.response?.data?.message || 'Failed to create department';
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMsg,
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const fetchDepartmentDetails = (departmentId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.get(`/org-people/departments/${departmentId}`);

    if (response?.data?.success || response?.status === 200) {
      const department = response.data.department || response.data;
      return department;
    } else {
      throw new Error('Failed to fetch department details');
    }
  } catch (error: any) {
    console.error('[ERROR] Error fetching department details:', error);
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to load department',
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const updateDepartment = (departmentId: string, departmentData: { name?: string; description?: string; practice_area_ids?: number[] }): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.put(`/org-people/departments/${departmentId}`, departmentData);

    if (response?.data?.success || response?.status === 200) {
      const department = response.data.department || response.data;
      
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: translate('Department updated successfully'),
          modalType: 'success',
          duration: 2000,
        })
      );
      return department;
    } else {
      throw new Error('Failed to update department');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to update department',
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const deleteDepartment = (departmentId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.delete(`/org-people/departments/${departmentId}`);

    if (response?.data?.success || response?.status === 200) {
      
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: translate('Department deleted successfully'),
          modalType: 'success',
          duration: 2000,
        })
      );
      return true;
    } else {
      throw new Error('Failed to delete department');
    }
  } catch (error: any) {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to delete department',
        modalType: 'error',
        duration: 3000,
      })
    );
    return false;
  }
};

export const addDepartmentMember = (departmentId: string, memberId: string, role: string = 'member'): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    const response = await apis.post(`/org-people/departments/${departmentId}/members`, {
      user_id: memberId,
      role,
    });

    if (response?.data?.success || response?.status === 200 || response?.status === 201) {
      const member = response.data.member || response.data;
      return member;
    } else {
      throw new Error('Failed to add member');
    }
  } catch (error: any) {
    console.error('[ERROR] Error adding member:', error);
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: readErrorMessage(error, 'Failed to add member'),
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const removeDepartmentMember = (departmentId: string, memberId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    
    const response = await apis.delete(`/org-people/departments/${departmentId}/members/${memberId}`);

    if (response?.data?.success || response?.status === 200) {
      return true;
    } else {
      throw new Error('Failed to remove member');
    }
  } catch (error: any) {
    console.error('[ERROR] Error removing member:', error);
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: readErrorMessage(error, 'Failed to remove member'),
        modalType: 'error',
        duration: 3000,
      })
    );
    return false;
  }
};

export const fetchDepartmentFeed = (departmentId: string): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    
    const response = await apis.get(`/org-people/departments/${departmentId}/feed`);

    if (response?.data?.success || response?.status === 200) {
      const feed = response.data.feed || [];
      return feed;
    } else {
      throw new Error('Failed to fetch feed');
    }
  } catch (error: any) {
    console.error('[ERROR] Error fetching department feed:', error);
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: readErrorMessage(error, 'Failed to load feed'),
        modalType: 'error',
        duration: 3000,
      })
    );
    return [];
  }
};

export const postDepartmentFeedEvent = (departmentId: string, eventData: { event_type: string; message: string; metadata?: any }): ThunkAction<Promise<any>, RootState, unknown, AnyAction> => async (dispatch) => {
  try {
    
    const response = await apis.post(`/org-people/departments/${departmentId}/feed`, eventData);

    if (response?.data?.success || response?.status === 200 || response?.status === 201) {
      const event = response.data.event || response.data;
      
      return event;
    } else {
      throw new Error('Failed to post event');
    }
  } catch (error: any) {
    console.error('[ERROR] Error posting feed event:', error);
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: readErrorMessage(error, 'Failed to post event'),
        modalType: 'error',
        duration: 3000,
      })
    );
    return null;
  }
};

export const fetchDepartmentMembers = (departmentId: string) => {
  return async (dispatch: Dispatch<any>) => {
    try {
      const response = await apis.get(`/org-people/departments/${departmentId}/members`);
      if (response && response.status === 200) {
        return response.data.members || [];
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * View a document from a department (display inline)
 * Implements streaming of encrypted file content
 * Uses REST API endpoint: GET /org-people/departments/{dept_uuid}/documents/{document_id}/view
 * Returns blob and metadata for displaying in a viewer
 */
export const viewDepartmentDocument = (
  departmentId: string,
  documentId: string,
  filename: string
): ThunkAction<Promise<{ success: boolean; blob?: Blob; contentType?: string; filename?: string; error?: string }>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/org-people/departments/${departmentId}/documents/${documentId}/view`,
        responseType: 'blob'
      });

      if (!response) {
        console.error('[viewDepartmentDocument] No response received from server');
        return {
          success: false,
          error: 'No response from server'
        };
      }

      if (response.status !== 200) {
        return {
          success: false,
          error: `Server returned status ${response.status}`
        };
      }

      const blob = response.data;

      if (!blob || !(blob instanceof Blob)) {
        return {
          success: false,
          error: 'Invalid file content received from server'
        };
      }

      if (blob.size === 0) {
        return {
          success: false,
          error: 'File is empty or could not be retrieved'
        };
      }

      if (blob.type === 'text/html' || blob.type === '') {
        return {
          success: false,
          error: 'Server returned an error. File may not exist or access was denied.'
        };
      }

      const contentType = ((response.headers['content-type'] as string) || '').toLowerCase().trim() || 'application/octet-stream';

      let viewFilename = filename;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
        if (filenameMatch) {
          viewFilename = filenameMatch[1]?.replace(/['"]/g, '') || filename;
        }
      }

      return {
        success: true,
        blob,
        contentType,
        filename: viewFilename
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Error viewing document'
      };
    }
  };

/**
 * Trigger browser download of a blob with specified filename
 * Cross-browser compatible helper function
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Download a document from a department with streaming support
 * Uses REST API endpoint: GET /org-people/departments/{dept_uuid}/documents/{document_id}/download
 * Returns blob that is saved to disk
 */
export const downloadDepartmentDocument = (departmentId: string, documentId: string, filename: string): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/org-people/departments/${departmentId}/documents/${documentId}/download`,
        responseType: 'blob'
      });

      if (!response) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('No response from server'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      if (response.status !== 200) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: `Server returned status ${response.status}`,
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      const blob = response.data;

      if (!blob || !(blob instanceof Blob)) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('Invalid file content received from server'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      if (blob.size === 0) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('File is empty or could not be retrieved'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      if (blob.type === 'text/html' || blob.type === '') {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('Server returned an error. File may not exist or access was denied.'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      const disposition = response.headers['content-disposition'];
      let downloadFilename = filename;
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1]?.replace(/['"]/g, '') || filename;
        }
      }

      triggerBlobDownload(blob, downloadFilename);
      return true;
    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: error.message || 'Error downloading document',
        showModal: true,
        duration: 3000
      }));
      return false;
    }
  };
